import siteConfig from "../modules/siteConfig";

/**
**  translations: use for `translate` directive
**/

Alpine.store("translations", {
    translations: Alpine.$persist({}).as("_x_translations"),
    translateBatchList: [],
    defaultLanguage: "en",    
    initializedCount: 0,
    // Count the total number of elements with `x-translate` attributes in the DOM
    get totalTranslationsCount() {
        let count = 0;
        Array.from(document.querySelectorAll("*")).forEach(el => {
          // Filter the attributes that start with "x-translate"
          const xTranslateAttributes = Array.from(el.attributes).filter(attr => attr.name.match(/^x-translate/));
          // Increment the count by the number of x-translate attributes
          count += xTranslateAttributes.length;
        });
        return count;
    },
    // Get the current language of the site (based on the locale)
    get currentLanguage() {
        return siteConfig.currentLocale.split("_")?.[0];
    },
    // Skip translations if the current language is the default language (English)
    get skipTranslations() {
        return this.currentLanguage === this.defaultLanguage;
    },
    // Check if all `x-translate` elements have been initialized and are ready for translation
    isReady() {
        return this.initializedCount >= this.totalTranslationsCount;
    },
    // Add the translated text to the store and dispatch the translation update
    addTranslation(translateText, translatedText, language = this.currentLanguage) {
        if (!this.translations[language]) {
            this.translations[language] = {};
        }

        this.translations[language][translateText] = translatedText;
        this.dispatchTranslations(translateText, translatedText);
    },
    // Retrieve the translated text from the store
    getTranslation(translateText, language = this.currentLanguage) {
        return this.translations[language]?.[translateText];
    },
    // Add text to translation batch list if not already present
    addToTranslateBatchList(translateText) {
        // Check if the text already exist on the list
        if (!this.translateBatchList.includes(translateText)) {
            this.translateBatchList.push(translateText);
        }

       // Increment initialized count
        this.initializedCount++;
    },
    // Dispatch translation event
    dispatchTranslations(translateText, translatedText) {
        window.dispatchEvent(new CustomEvent("translate", { detail: { translateText, translatedText } }));
    },
    // Translate text
    async translate(translateText, language = this.currentLanguage) {
        // Check if the text has already been translated
        const existingTranslation = this.getTranslation(translateText);
        if (existingTranslation) {
            // Dispatch the existing translation
            this.dispatchTranslations(translateText, existingTranslation);
            return;
        }

        // Avoid duplicate translation if the text is already in the batch list
        if (!this.translateBatchList.includes(translateText)) {
            // Execute translation
            const { default: translate } = await import("translate");
            const translatedText = await translate(translateText, language);
            // Add translated text to store
            this.addTranslation(translateText, translatedText, language);
        }
    },
    // Translate texts in batch
    async translateInBatch(language = this.currentLanguage) {
        // Get the list of texts that need translation
        const translateBatchList = await this.getTranslateBatchList();
        // Exit if no texts need translation
        if (!translateBatchList.length) return;

        // Execute batch translation
        const separator = " ~ ";
        const { default: translate } = await import("translate");
        const translatedTextList = await translate(translateBatchList.join(separator), language);

        // Add translated texts for each item in the list to store
        translateBatchList.forEach((translateText, index) => {
            const translatedText = translatedTextList.split(separator)?.[index];
            this.addTranslation(translateText, translatedText, language);
        });
    },
    // Get the list of texts to translate in batch
    async getTranslateBatchList() {
        try {
            const translations = siteConfig.translationsPath;
            if (translations) {
                // Fetch Translation Override data
                const $fetch = Alpine.evaluate(document.body, "$fetch");
                const response = await $fetch.get(translations);
                const translationsOverride = await response.json();
                // Merge the translation overrides into the existing translations
                this.translations = this.overrideTranslations(this.translations, translationsOverride);
            }
        } catch (e) {
            console.error("Failed to fetch overrides:", e);
        }

        // Filter the translation list to exclude already translated texts
        return this.translateBatchList.filter(translateText => {
            const translatedText = this.getTranslation(translateText);
            if (translatedText) {
                this.dispatchTranslations(translateText, translatedText); // Render already translated texts
                return false; // Already translated, so don't include it
            }
            return true; // Needs translation
        });
    },
    // Helper function to deeply merge multiple translation objects
    overrideTranslations(...translations) {
        return translations.reduce((acc, obj) => {
            Object.keys(obj).forEach((key) => {
                if (typeof obj[key] === "object" && typeof acc[key] === "object") {
                    acc[key] = this.overrideTranslations(acc[key], obj[key]);
                } else {
                    acc[key] = obj[key];
                }
            });
            return acc;
        }, {});
    },
    // Render the translated text to the DOM element
    renderTranslation(el, attributeName, translateFrom, translatedTo) {
        if (attributeName) {
            el.setAttribute(attributeName, translatedTo || translateFrom);
        } else {
            el.innerText = translatedTo || translateFrom;
        }
        // Bind Translations
        if (!el._x_translations) el._x_translations = {};
        el._x_translations[attributeName ?? "text"] = { [translateFrom]: translatedTo ?? translateFrom };
    }
});